// Adapted from HestiaDoors / Cottage / C10-GL081 - Pine Thresh in hestia2.
// CNC removes 5 mm from every aperture edge (user-specified allowance).
// Studio openings are already finished cutouts, so only the core preparation
// retains 5 mm per edge. The configured glazing geometry is never resized.
export const COTTAGE_OPENING_TRIM_MM = 5;
const mm = n => Number(n.toFixed(4));
const box = (x,y,w,h) => ({xMm:mm(x),yMm:mm(y),widthMm:mm(w),heightMm:mm(h)});
const area = b => b.widthMm*b.heightMm;
const right = b => b.xMm+b.widthMm;
const top = b => b.yMm+b.heightMm;
const valid = b => b.widthMm>0.0001 && b.heightMm>0.0001;

function intersection(a,b) {
  const x=Math.max(a.xMm,b.xMm),y=Math.max(a.yMm,b.yMm);
  return box(x,y,Math.min(right(a),right(b))-x,Math.min(top(a),top(b))-y);
}

function subtract(a,b) {
  const hit=intersection(a,b);
  if(!valid(hit))return [a];
  return [box(a.xMm,a.yMm,a.widthMm,hit.yMm-a.yMm),
    box(a.xMm,top(hit),a.widthMm,top(a)-top(hit)),
    box(a.xMm,hit.yMm,hit.xMm-a.xMm,hit.heightMm),
    box(right(hit),hit.yMm,right(a)-right(hit),hit.heightMm)].filter(valid);
}

function merge(rects) {
  const result=rects.slice(), near=(a,b)=>Math.abs(a-b)<0.0001;
  let changed=true;
  while(changed){
    changed=false;
    outer: for(let i=0;i<result.length;i++)for(let j=i+1;j<result.length;j++){
      const a=result[i],b=result[j];
      const vertical=near(a.xMm,b.xMm)&&near(a.widthMm,b.widthMm)&&(near(top(a),b.yMm)||near(top(b),a.yMm));
      const horizontal=near(a.yMm,b.yMm)&&near(a.heightMm,b.heightMm)&&(near(right(a),b.xMm)||near(right(b),a.xMm));
      if(vertical||horizontal){
        result[i]=box(Math.min(a.xMm,b.xMm),Math.min(a.yMm,b.yMm),horizontal?a.widthMm+b.widthMm:a.widthMm,vertical?a.heightMm+b.heightMm:a.heightMm);
        result.splice(j,1);changed=true;break outer;
      }
    }
  }
  return result;
}

export function cottageGlazedCore(core,geometry,product,lock) {
  const w=core.widthMm,h=core.heightMm,r=core.railMm,s=core.stileMm;
  const offset=core.allowanceMm/2,trim=COTTAGE_OPENING_TRIM_MM;
  const wr=mm(Math.max(r,(geometry.cassette?.overlapMm??0)+trim));
  const blank=box(0,0,w,h),parts=[],issues=[];
  const finishedOpenings=geometry.openings.map((o,i)=>({...box(o.xMm+offset,o.yMm+offset,o.widthMm,o.heightMm),openingIndex:i}));
  const apertures=finishedOpenings.map(o=>({...box(o.xMm+trim,o.yMm+trim,o.widthMm-2*trim,o.heightMm-2*trim),openingIndex:o.openingIndex}));
  if(apertures.some(a=>!valid(a)))throw new Error('Cottage opening is too small for the core trimming allowance.');
  const candidates=[];
  const candidate=(name,role,x,y,width,height,openingIndex)=>candidates.push({name,role,...box(x,y,width,height),...(openingIndex===undefined?{}:{openingIndex})});
  candidate('Left stile','perimeter',0,0,s,h);
  candidate('Right stile','perimeter',w-s,0,s,h);
  candidate('Bottom rail','perimeter',s,0,w-2*s,r);
  candidate('Top rail','perimeter',s,h-r,w-2*s,r);
  const groupedOpenings=new Set(),windowGroups=[];
  const columns=[];
  for(const a of apertures){
    let column=columns.find(items=>Math.abs(items[0].xMm-a.xMm)<0.01&&Math.abs(items[0].widthMm-a.widthMm)<0.01);
    if(!column){column=[];columns.push(column);}column.push(a);
  }
  for(const column of columns){
    if(column.length<2)continue;
    column.sort((a,b)=>a.yMm-b.yMm);
    const first=column[0],last=column.at(-1),span=top(last)-first.yMm;
    // Compare timber area before committing: very widely spaced windows can
    // need more timber in continuous stiles than in independent supports.
    const independent=column.reduce((sum,a)=>sum+(a.heightMm>2*a.widthMm
      ?2*wr*(h-2*r)+2*wr*a.widthMm:2*wr*(w-2*s)+2*wr*a.heightMm),0);
    const shared=2*wr*(w-2*s)+2*wr*span+(column.length-1)*2*wr*first.widthMm;
    if(shared>=independent)continue;
    const envelope=box(first.xMm-wr,first.yMm-wr,first.widthMm+2*wr,span+2*wr);
    if(apertures.some(a=>!column.includes(a)&&valid(intersection(envelope,a))))continue;
    const group=windowGroups.length+1;
    candidate(`Window group ${group} lower rail`,'window-rail',s,first.yMm-wr,w-2*s,wr);
    candidate(`Window group ${group} upper rail`,'window-rail',s,top(last),w-2*s,wr);
    candidate(`Window group ${group} left stile`,'window-stile',first.xMm-wr,first.yMm,wr,span);
    candidate(`Window group ${group} right stile`,'window-stile',right(first),first.yMm,wr,span);
    for(let i=0;i<column.length-1;i++){
      const lower=column[i],upper=column[i+1];
      candidate(`Window group ${group} crosspiece ${i+1} lower`,'window-rail',first.xMm,top(lower),first.widthMm,wr);
      candidate(`Window group ${group} crosspiece ${i+1} upper`,'window-rail',first.xMm,upper.yMm-wr,first.widthMm,wr);
    }
    column.forEach(a=>groupedOpenings.add(a.openingIndex));
    windowGroups.push({openingIndices:column.map(a=>a.openingIndex),layout:'shared-horizontal-rails'});
  }
  for(const a of apertures){
    const n=a.openingIndex+1;
    // Narrow upright apertures use continuous stiles; other glazing uses
    // continuous cross rails with short stiles fitted between them.
    const vertical=a.heightMm>2*a.widthMm;
    if(groupedOpenings.has(a.openingIndex)){
      // Supports already belong to the shared window group.
    }else if(vertical){
      candidate(`Window ${n} left stile`,'window-stile',a.xMm-wr,r,wr,h-2*r,a.openingIndex);
      candidate(`Window ${n} right stile`,'window-stile',right(a),r,wr,h-2*r,a.openingIndex);
      candidate(`Window ${n} lower rail`,'window-rail',a.xMm,a.yMm-wr,a.widthMm,wr,a.openingIndex);
      candidate(`Window ${n} upper rail`,'window-rail',a.xMm,top(a),a.widthMm,wr,a.openingIndex);
    }else{
      candidate(`Window ${n} lower rail`,'window-rail',s,a.yMm-wr,w-2*s,wr,a.openingIndex);
      candidate(`Window ${n} upper rail`,'window-rail',s,top(a),w-2*s,wr,a.openingIndex);
      candidate(`Window ${n} left stile`,'window-stile',a.xMm-wr,a.yMm,wr,a.heightMm,a.openingIndex);
      candidate(`Window ${n} right stile`,'window-stile',right(a),a.yMm,wr,a.heightMm,a.openingIndex);
    }
    if(a.xMm<wr || right(a)>w-wr || a.yMm<wr || top(a)>h-wr)issues.push(`Window ${n}: the full reference rail width does not fit between the opening and the core edge.`);
    const otherApertures=apertures.filter(o=>o!==a);
    if(candidates.filter(c=>c.openingIndex===a.openingIndex).some(c=>otherApertures.some(o=>valid(intersection(c,o)))))
      issues.push(`Window ${n}: another opening interrupts a full-height window stile; review its joints.`);
  }
  // Keep the reference 607 mm lock reinforcement, located from the actual
  // hardware datum rather than assuming every configured lock is centred.
  const lockHeight=Math.min(607,h-2*r),lockY=Math.max(r,Math.min(h-r-lockHeight,lock.centerMm+offset-lockHeight/2));
  const lockBlock=box(product.handing==='right'?s:w-s-r,lockY,r,lockHeight);
  candidate('Lock block','lock-block',lockBlock.xMm,lockBlock.yMm,r,lockHeight);
  if(finishedOpenings.some(o=>valid(intersection(lockBlock,o))))issues.push('The finished window cutout intersects the lock reinforcement; revise the opening or hardware position.');

  // Earlier members own shared timber. Subtract the actual apertures before
  // adding a member, so extra windows cannot acquire rails across the glass.
  for(const c of candidates){
    const clipped=intersection(c,blank);
    if(!valid(clipped))continue;
    let pieces=[clipped];
    for(const occupied of [...apertures,...parts])pieces=pieces.flatMap(p=>subtract(p,occupied));
    pieces=merge(pieces);
    pieces.forEach((p,i)=>parts.push({...p,name:c.name+(pieces.length>1?` / ${i+1}`:''),material:'timber',role:c.role,openingIndex:c.openingIndex}));
  }
  let insulation=[blank];
  for(const occupied of [...apertures,...parts])insulation=insulation.flatMap(p=>subtract(p,occupied));
  merge(insulation).sort((a,b)=>a.xMm-b.xMm||a.yMm-b.yMm).forEach((p,i)=>parts.push({...p,name:`EPS ${i+1}`,material:'EPS',role:'insulation'}));
  return {parts,issues,apertures,finishedOpenings,windowGroups,openingTrimMm:trim,windowRailMm:wr,
    reference:'hestia2/Cottage/C10-GL081 - Pine Thresh',
    timberAreaMm2:mm(parts.filter(p=>p.material==='timber').reduce((n,p)=>n+area(p),0))};
}
